import React, { useState, useRef, useEffect } from 'react';
import { useInstitution } from '../contexts/InstitutionContext';

const InstitutionSwitcher: React.FC = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 10, y: 10 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  // Handle drag move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Keep panel within viewport bounds
        const maxX = window.innerWidth - (panelRef.current?.offsetWidth || 250);
        const maxY = window.innerHeight - (panelRef.current?.offsetHeight || 100);
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Add error boundary and debugging
  let institutionContextData;
  try {
    institutionContextData = useInstitution();
  } catch (error) {
    console.error('InstitutionSwitcher: Error accessing institution context:', error);
    return (
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          background: '#fff',
          border: '2px solid red',
          borderRadius: '8px',
          padding: '10px',
          zIndex: 9999,
          fontSize: '12px',
          minWidth: '200px',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleMouseDown}
      >
        <div>🏫 Institution Context Error</div>
        <div style={{ color: 'red', fontSize: '10px' }}>
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  const {
    institution,
    detectionInfo,
    loading,
    error,
    theme,
    availableInstitutions,
    switchInstitution,
    refreshInstitution,
    refreshAvailableInstitutions,
    isInstitutionContext,
    institutionName
  } = institutionContextData;

  if (loading) {
    return (
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          background: '#fff',
          border: '2px solid #ddd',
          borderRadius: '8px',
          padding: '10px',
          zIndex: 9999,
          fontSize: '12px',
          minWidth: '200px',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleMouseDown}
      >
        <div>🏫 Loading institution...</div>
      </div>
    );
  }

  // If minimized, show only a small collapsed version
  if (isMinimized) {
    return (
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          background: '#fff',
          border: '2px solid #ddd',
          borderRadius: '8px',
          padding: '8px',
          zIndex: 9999,
          fontSize: '12px',
          cursor: isDragging ? 'grabbing' : 'grab',
          minWidth: '120px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}
        onMouseDown={handleMouseDown}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>🏫 {institutionName}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(false);
            }}
            style={{
              fontSize: '10px',
              padding: '2px 6px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ⬆
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        background: '#fff',
        border: '2px solid #ddd',
        borderRadius: '8px',
        padding: '10px',
        zIndex: 9999,
        fontSize: '12px',
        minWidth: '250px',
        maxWidth: '350px',
        cursor: isDragging ? 'grabbing' : 'grab',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
    >
      <div style={{ 
        fontWeight: 'bold', 
        marginBottom: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>🏫 Institution Debug Panel</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized(true);
          }}
          style={{
            fontSize: '10px',
            padding: '2px 6px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#666'
          }}
        >
          ⬇
        </button>
      </div>
      
      {error && (
        <div style={{ color: 'red', marginBottom: '8px' }}>
          Error: {error}
        </div>
      )}
      
      <div style={{ marginBottom: '8px' }}>
        <strong>Current:</strong> {isInstitutionContext ? institutionName : 'Default Maxel'}
      </div>
      
      {institution && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: theme.primary_color,
            display: 'inline-block',
            marginRight: '8px',
            border: '1px solid #ccc'
          }}></div>
          <span>Theme: {theme.primary_color}</span>
        </div>
      )}
      
      <div style={{ marginBottom: '8px' }}>
        <strong>Detection:</strong> {detectionInfo?.detection_method || 'none'}
      </div>
      
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowDetails(!showDetails);
        }}
        style={{
          fontSize: '11px',
          padding: '4px 8px',
          marginRight: '8px',
          marginBottom: '8px',
          cursor: 'pointer'
        }}
      >
        {showDetails ? 'Hide' : 'Show'} Details
      </button>
      
      <button
        onClick={(e) => {
          e.stopPropagation();
          refreshInstitution();
        }}
        style={{
          fontSize: '11px',
          padding: '4px 8px',
          marginBottom: '8px',
          cursor: 'pointer'
        }}
      >
        Refresh
      </button>
      
      {showDetails && (
        <div style={{ 
          marginTop: '8px', 
          padding: '8px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '4px',
          fontSize: '11px' 
        }}>
          <div><strong>URL:</strong> {detectionInfo?.current_url}</div>
          <div><strong>Environment:</strong> {detectionInfo?.environment}</div>
          {detectionInfo?.subdomain_extracted && (
            <div><strong>Subdomain:</strong> {detectionInfo.subdomain_extracted}</div>
          )}
          {detectionInfo?.query_param_value && (
            <div><strong>Query Param:</strong> {detectionInfo.query_param_value}</div>
          )}
          {institution && (
            <>
              <div><strong>Institution ID:</strong> {institution.id}</div>
              <div><strong>Custom URL:</strong> {institution.custom_url}</div>
              {institution.logo_url && (
                <div><strong>Logo:</strong> {institution.logo_url}</div>
              )}
            </>
          )}
        </div>
      )}
      
      <div style={{ marginTop: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <strong>Switch Institution:</strong>
          <button
            onClick={(e) => {
              e.stopPropagation();
              refreshAvailableInstitutions();
            }}
            style={{
              fontSize: '10px',
              padding: '2px 6px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            🔄 Refresh
          </button>
        </div>
        <div style={{ marginTop: '4px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              switchInstitution('none');
            }}
            style={{
              fontSize: '11px',
              padding: '4px 8px',
              marginRight: '4px',
              marginBottom: '4px',
              backgroundColor: !isInstitutionContext ? '#e0e0e0' : '#fff',
              cursor: 'pointer'
            }}
          >
            Default
          </button>
          {Array.isArray(availableInstitutions) && availableInstitutions.map((inst) => (
            <button
              key={inst.subdomain}
              onClick={(e) => {
                e.stopPropagation();
                switchInstitution(inst.subdomain);
              }}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                marginRight: '4px',
                marginBottom: '4px',
                backgroundColor: institution?.subdomain === inst.subdomain ? '#e0e0e0' : '#fff',
                cursor: 'pointer'
              }}
            >
              {inst.name}
            </button>
          ))}
          {!Array.isArray(availableInstitutions) && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              No institutions available
            </div>
          )}
        </div>
      </div>
      
      <div style={{
        marginTop: '8px',
        padding: '4px',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        fontSize: '10px',
        color: '#666',
        textAlign: 'center'
      }}>
        💡 Drag to move • Click ⬇ to minimize
      </div>
    </div>
  );
};

export default InstitutionSwitcher; 